import React, { useState, useRef } from 'react';
import { Upload, AlertCircle, CheckCircle, Instagram, Facebook, Linkedin } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';


const COUNTRIES = ['United States', 'United Kingdom', 'Canada', 'Australia', 'Germany', 'France', 'India', 'Singapore', 'UAE', 'South Africa', 'Brazil', 'Japan', 'Other'];
const CATEGORIES = ['Retail', 'Electronics', 'Groceries', 'Fashion', 'Health & Beauty', 'Sports', 'Home & Living', 'Other'];

export default function Step2CompanyProfile({ partnerProfile, partnerUser, onComplete }) {
  const [form, setForm] = useState({
    business_name: partnerProfile?.business_name ?? '',
    tagline: partnerProfile?.tagline ?? '',
    contact_email: partnerProfile?.contact_email ?? partnerUser?.email ?? '',
    phone: partnerProfile?.phone ?? '',
    website_url: partnerProfile?.website_url ?? '',
    street: partnerProfile?.physical_address?.street ?? '',
    city: partnerProfile?.physical_address?.city ?? '',
    country: partnerProfile?.physical_address?.country ?? '',
    category: partnerProfile?.business_category ?? '',
    brand_colour: partnerProfile?.brand_colour ?? '#00E5FF',
    instagram: partnerProfile?.social_links?.instagram ?? '',
    facebook: partnerProfile?.social_links?.facebook ?? '',
    linkedin: partnerProfile?.social_links?.linkedin ?? '',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(partnerProfile?.logo_url ?? '');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);

  const inputStyle = {
    background: 'rgba(0,0,0,0.3)',
    border: '1px solid rgba(0,229,255,0.15)',
    fontFamily: 'DM Sans, sans-serif',
  };

  const handleLogoChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (file?.size > 2 * 1024 * 1024) { setError('Logo must be under 2MB.'); return; }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setError('');
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    const file = e?.dataTransfer?.files?.[0];
    if (!file) return;
    if (file?.size > 2 * 1024 * 1024) { setError('Logo must be under 2MB.'); return; }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setError('');
  };

  const handleSave = async () => {
    if (!form?.business_name?.trim()) { setError('Company name is required.'); return; }
    setError('');
    setSaving(true);
    try {
      let logoUrl = partnerProfile?.logo_url ?? '';

      // Upload logo if selected
      if (logoFile) {
        setUploading(true);
        const ext = logoFile?.name?.split('.')?.pop();
        const fileName = `${partnerProfile?.id ?? 'logo'}-${Date.now()}.${ext}`;
        const { data: uploadData, error: uploadErr } = await supabase?.storage
          ?.from('partner-logos')
          ?.upload(fileName, logoFile, { upsert: true });
        setUploading(false);
        if (uploadErr) { setError('Logo upload failed: ' + uploadErr?.message); setSaving(false); return; }
        const { data: urlData } = supabase?.storage?.from('partner-logos')?.getPublicUrl(fileName);
        logoUrl = urlData?.publicUrl ?? '';
      }

      const { error: updateErr } = await supabase
        ?.from('partner_profiles')
        ?.update({
          business_name: form?.business_name,
          tagline: form?.tagline,
          contact_email: form?.contact_email,
          phone: form?.phone,
          website_url: form?.website_url,
          logo_url: logoUrl,
          brand_colour: form?.brand_colour,
          physical_address: { street: form?.street, city: form?.city, country: form?.country },
          social_links: { instagram: form?.instagram, facebook: form?.facebook, linkedin: form?.linkedin },
          onboarding_step: 3,
        })
        ?.eq('id', partnerProfile?.id);

      if (updateErr) { setError(updateErr?.message); return; }

      setSuccess(true);
      setTimeout(() => onComplete?.(), 1000);
    } catch (e) {
      setError('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto px-8 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'DM Sans, sans-serif' }}>Tell us about your business</h1>
        <p className="text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>This information will appear in the FinVisAR AR Scanner for consumers.</p>
      </div>
      {success ? (
        <div className="flex items-center gap-3 p-4 rounded-xl" style={{ background: 'rgba(0,229,255,0.08)', border: '1px solid rgba(0,229,255,0.25)' }}>
          <CheckCircle size={20} style={{ color: '#00E5FF' }} />
          <p className="text-sm font-semibold" style={{ color: '#00E5FF', fontFamily: 'DM Sans, sans-serif' }}>Profile saved! Moving to next step...</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Logo upload */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Company Logo</label>
            <div
              className="relative flex flex-col items-center justify-center rounded-2xl cursor-pointer transition-all duration-200"
              style={{
                border: '2px dashed rgba(0,229,255,0.3)',
                background: 'rgba(0,229,255,0.03)',
                minHeight: '140px',
              }}
              onClick={() => fileInputRef?.current?.click()}
              onDrop={handleDrop}
              onDragOver={e => e?.preventDefault()}
            >
              {logoPreview ? (
                <div className="flex flex-col items-center gap-2 p-4">
                  <img src={logoPreview} alt="Company logo preview" className="w-20 h-20 object-contain rounded-xl" style={{ border: '1px solid rgba(0,229,255,0.2)' }} />
                  <p className="text-xs" style={{ color: '#00E5FF', fontFamily: 'DM Sans, sans-serif' }}>Click to change logo</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2 p-6">
                  <Upload size={28} style={{ color: '#00E5FF' }} />
                  <p className="text-sm font-medium" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Drop your logo here or click to upload</p>
                  <p className="text-xs" style={{ color: '#475569', fontFamily: 'DM Sans, sans-serif' }}>PNG, JPG, SVG up to 2MB</p>
                </div>
              )}
              <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/svg+xml" className="hidden" onChange={handleLogoChange} />
            </div>
          </div>

          {/* Business details */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-xs font-medium mb-1.5" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Company Display Name <span style={{ color: '#EF4444' }}>*</span></label>
              <input type="text" value={form?.business_name} onChange={e => setForm(f => ({ ...f, business_name: e?.target?.value }))} placeholder="Your company name"
                className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-slate-600 outline-none" style={inputStyle} />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium mb-1.5" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Tagline / Short Description <span className="text-xs" style={{ color: '#475569' }}>({form?.tagline?.length ?? 0}/100)</span></label>
              <input type="text" value={form?.tagline} onChange={e => setForm(f => ({ ...f, tagline: e?.target?.value?.slice(0, 100) }))} placeholder="Shown in AR Scanner to consumers"
                className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-slate-600 outline-none" style={inputStyle} />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Business Email</label>
              <input type="email" value={form?.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e?.target?.value }))} placeholder="contact@company.com"
                className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-slate-600 outline-none" style={inputStyle} />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Phone Number</label>
              <input type="tel" value={form?.phone} onChange={e => setForm(f => ({ ...f, phone: e?.target?.value }))} placeholder="+1 555 000 0000"
                className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-slate-600 outline-none" style={inputStyle} />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium mb-1.5" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Website URL</label>
              <input type="url" value={form?.website_url} onChange={e => setForm(f => ({ ...f, website_url: e?.target?.value }))} placeholder="https://yourcompany.com"
                className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-slate-600 outline-none" style={inputStyle} />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Street Address</label>
              <input type="text" value={form?.street} onChange={e => setForm(f => ({ ...f, street: e?.target?.value }))} placeholder="123 Main St"
                className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-slate-600 outline-none" style={inputStyle} />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>City</label>
              <input type="text" value={form?.city} onChange={e => setForm(f => ({ ...f, city: e?.target?.value }))} placeholder="Your city"
                className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-slate-600 outline-none" style={inputStyle} />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Country</label>
              <select value={form?.country} onChange={e => setForm(f => ({ ...f, country: e?.target?.value }))}
                className="w-full px-4 py-3 rounded-xl text-sm outline-none" style={{ ...inputStyle, color: form?.country ? '#E2E8F0' : '#64748B' }}>
                <option value="" style={{ background: '#0D1B2A' }}>Select country</option>
                {COUNTRIES?.map(c => <option key={c} value={c} style={{ background: '#0D1B2A' }}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Business Category</label>
              <select value={form?.category} onChange={e => setForm(f => ({ ...f, category: e?.target?.value }))}
                className="w-full px-4 py-3 rounded-xl text-sm outline-none" style={{ ...inputStyle, color: form?.category ? '#E2E8F0' : '#64748B' }}>
                <option value="" style={{ background: '#0D1B2A' }}>Select category</option>
                {CATEGORIES?.map(c => <option key={c} value={c} style={{ background: '#0D1B2A' }}>{c}</option>)}
              </select>
            </div>
          </div>

          {/* Brand colour */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Brand Colour</label>
            <div className="flex items-center gap-3">
              <div className="relative">
                <input
                  type="color"
                  value={form?.brand_colour}
                  onChange={e => setForm(f => ({ ...f, brand_colour: e?.target?.value }))}
                  className="w-12 h-12 rounded-xl cursor-pointer border-0 p-1"
                  style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,229,255,0.15)' }}
                />
              </div>
              <input
                type="text"
                value={form?.brand_colour}
                onChange={e => setForm(f => ({ ...f, brand_colour: e?.target?.value }))}
                placeholder="#00E5FF"
                className="w-36 px-4 py-3 rounded-xl text-sm text-white placeholder-slate-600 outline-none"
                style={inputStyle}
              />
              <div className="w-10 h-10 rounded-xl" style={{ background: form?.brand_colour, border: '1px solid rgba(255,255,255,0.1)' }} />
              <p className="text-xs" style={{ color: '#475569', fontFamily: 'DM Sans, sans-serif' }}>Used as accent in AR Scanner product cards</p>
            </div>
          </div>

          {/* Social links */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Social Links <span style={{ color: '#334155' }}>(Optional)</span></label>
            <div className="space-y-3">
              {[
                { key: 'instagram', icon: Instagram, placeholder: 'https://instagram.com/yourcompany', label: 'Instagram' },
                { key: 'facebook', icon: Facebook, placeholder: 'https://facebook.com/yourcompany', label: 'Facebook' },
                { key: 'linkedin', icon: Linkedin, placeholder: 'https://linkedin.com/company/yourcompany', label: 'LinkedIn' },
              ]?.map(({ key, icon: Icon, placeholder, label }) => (
                <div key={key} className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,229,255,0.1)' }}>
                    <Icon size={15} style={{ color: '#64748B' }} />
                  </div>
                  <input
                    type="url"
                    value={form?.[key]}
                    onChange={e => setForm(f => ({ ...f, [key]: e?.target?.value }))}
                    placeholder={placeholder}
                    className="flex-1 px-4 py-2.5 rounded-xl text-sm text-white placeholder-slate-600 outline-none"
                    style={inputStyle}
                  />
                </div>
              ))}
            </div>
          </div>

          {error && (
            <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
              <AlertCircle size={14} className="mt-0.5 flex-shrink-0" style={{ color: '#EF4444' }} />
              <p className="text-xs" style={{ color: '#EF4444', fontFamily: 'DM Sans, sans-serif' }}>{error}</p>
            </div>
          )}

          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-3 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2"
            style={{
              background: saving ? 'rgba(0,229,255,0.3)' : 'linear-gradient(135deg, #00E5FF, #7B2FBE)',
              fontFamily: 'DM Sans, sans-serif',
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
            {uploading ? 'Uploading logo...' : saving ? 'Saving profile...' : 'Save & Continue'}
          </button>
        </div>
      )}
    </div>
  );
}
